import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { appVendorCasesQuery, caseSettingsQuery, saveCaseSettings, type CaseSettings, type VendorCase } from '../api/vendorCases'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { failed } from './parts'
import './AppVendorCases.css'

/**
 * "Vendor cases" pe fisa aplicatiei: cazurile deschise la furnizor (din
 * tichetele clientului), cu termenul primului raspuns si starea, cele peste
 * termen intai; "Show closed" aduce si istoria.
 *
 * Sub lista, setarile termenului: calendarul sarbatorilor legale (Romania
 * implicit), zilele libere anuntate de furnizor si un exemplu de numar de caz
 * ("TS-12345"), din care numarul se ia singur din raspunsul lor.
 *
 * Fara dreptul pe tichete (lista) sau inainte de migrare, cardul nu apare.
 */
export function AppVendorCases({ appId }: { appId: number }) {
  const [all, setAll] = useState(false)
  const [editing, setEditing] = useState(false)
  const cases = useQuery({ ...appVendorCasesQuery(appId, all), retry: false })
  const settings = useQuery({ ...caseSettingsQuery(appId), retry: false })

  if (!cases.data?.ready || !Array.isArray(cases.data.cases)) return null
  const list = [...cases.data.cases].sort((a, b) => Number(b.overdue) - Number(a.overdue))
  const s = settings.data?.ready ? settings.data : null

  return (
    <>
      <div className="adm-section">
        <h4>Vendor cases</h4>
        <span className="adm-section-note">escalations to the vendor, until they are closed</span>
        <label className="adm-muted small" style={{ marginLeft: 'auto', fontWeight: 'normal' }}>
          <input type="checkbox" id="vendorCasesAll" checked={all} onChange={(ev) => setAll(ev.target.checked)} /> Show closed
        </label>
      </div>
      <div className="adm-card" id="appVendorCases">
        {list.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            {all ? 'No vendor cases yet.' : 'No open vendor cases.'} Escalating from a ticket opens one.
          </p>
        ) : (
          <table className="table adm-list">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Vendor case</th>
                <th>Status</th>
                <th>First response</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <CaseLine key={c.id} item={c} />
              ))}
            </tbody>
          </table>
        )}
        {s && (
          <div className="vc-settings">
            <span className="adm-muted small">
              Due dates skip {s.holidayCountry === '' ? 'no public holidays' : `public holidays (${countryName(s)})`}
              {(s.daysOff ?? []).length > 0 ? ` and ${(s.daysOff ?? []).length} vendor day${(s.daysOff ?? []).length === 1 ? '' : 's'} off` : ''}
              {s.caseRefPattern ? ` · case numbers like ${s.caseRefPattern}` : ''}
            </span>
            {s.canEdit && (
              <button type="button" id="caseSettingsEdit" className="btn btn-default btn-xs" onClick={() => setEditing(true)}>
                <i className="fa fa-calendar-alt"></i> Holidays &amp; case numbers
              </button>
            )}
          </div>
        )}
      </div>
      {editing && s && <SettingsModal appId={appId} settings={s} onClose={() => setEditing(false)} />}
    </>
  )
}

function countryName(s: CaseSettings): string {
  return (s.countries ?? []).find((c) => c.value === s.holidayCountry)?.label ?? s.holidayCountry ?? ''
}

function CaseLine({ item }: { item: VendorCase }) {
  return (
    <tr className={item.overdue ? 'vc-overdue' : undefined}>
      <td>
        <a href={`/app/easeedesk/ticket/${item.ticketId}`} target="_blank" rel="noopener noreferrer">
          #{item.ticketId} {item.ticketSubject}
        </a>
        <div className="adm-muted small">sent {item.sentAt}</div>
      </td>
      <td>
        {item.supportName}
        {item.vendorRef !== '' && <strong> {item.vendorRef}</strong>}
        {item.level > 0 && <div className="adm-muted small">level {item.level}</div>}
      </td>
      <td>
        <span className={`adm-pill ${item.overdue ? 'adm-pill-bad' : item.status >= 5 ? 'adm-pill-ok' : 'adm-pill-warn'}`}>{item.statusLabel}</span>
      </td>
      <td className="adm-nowrap">
        {item.awaitingResponse ? (
          item.dueAt !== null ? (
            <span className={item.overdue ? 'text-danger' : undefined}>
              due {item.dueAt}
              {item.overdue && <strong> - overdue</strong>}
            </span>
          ) : (
            <span className="adm-muted">waiting, no due time</span>
          )
        ) : item.respondedAt !== null ? (
          <span className="adm-muted">answered {item.respondedAt}</span>
        ) : (
          <span className="adm-muted">-</span>
        )}
      </td>
    </tr>
  )
}

function SettingsModal({ appId, settings, onClose }: { appId: number; settings: CaseSettings; onClose(): void }) {
  const query = useQueryClient()
  const [country, setCountry] = useState(settings.holidayCountry ?? 'RO')
  const [pattern, setPattern] = useState(settings.caseRefPattern ?? '')
  const [days, setDays] = useState<{ day: string; note: string }[]>(settings.daysOff ?? [])
  const [busy, setBusy] = useState(false)

  const save = () => {
    setBusy(true)
    saveCaseSettings(appId, { holiday_country: country, case_ref_pattern: pattern, days_off: days.filter((d) => d.day !== '') })
      .then((r) => {
        toast(r.message, 'Vendor cases', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') {
          void query.invalidateQueries({ queryKey: ['business-apps', 'case-settings', appId] })
          onClose()
        }
      })
      .catch((e) => toast(failed(e), 'Vendor cases', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id={`caseSettings-${appId}`}
      open
      onClose={onClose}
      title="Vendor case due dates"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" id="caseSettingsSave" disabled={busy} onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="vcCountry">Public holidays</label>
        <select id="vcCountry" className="form-control" value={country} onChange={(ev) => setCountry(ev.target.value)}>
          {(settings.countries ?? []).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        {country === settings.holidayCountry && (settings.upcomingHolidays ?? []).length > 0 && (
          <p className="help-block">
            Next: {(settings.upcomingHolidays ?? []).slice(0, 4).map((h) => `${h.day} ${h.name}`).join(', ')}
          </p>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="vcPattern">Vendor case number looks like</label>
        <input id="vcPattern" className="form-control" placeholder="e.g. TS-12345" value={pattern} maxLength={100} onChange={(ev) => setPattern(ev.target.value)} />
        <p className="help-block">When the vendor's reply has a number like this in its subject, the case takes it.</p>
      </div>
      <div className="form-group">
        <label>Vendor days off</label>
        {days.map((d, i) => (
          <div key={i} className="vc-dayoff">
            <input
              type="date"
              className="form-control input-sm"
              aria-label="Day off"
              value={d.day}
              onChange={(ev) => setDays(days.map((x, j) => (j === i ? { ...x, day: ev.target.value } : x)))}
            />
            <input
              className="form-control input-sm"
              aria-label="Note"
              placeholder="note"
              value={d.note}
              maxLength={190}
              onChange={(ev) => setDays(days.map((x, j) => (j === i ? { ...x, note: ev.target.value } : x)))}
            />
            <button type="button" className="btn btn-link btn-xs" aria-label="Remove day off" onClick={() => setDays(days.filter((_, j) => j !== i))}>
              <i className="fa fa-times"></i>
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-default btn-xs" id="vcAddDay" onClick={() => setDays([...days, { day: '', note: '' }])}>
          <i className="fa fa-plus"></i> Add a day off
        </button>
        <p className="help-block">Changing these does not move the due dates of cases already sent.</p>
      </div>
    </Modal>
  )
}
